import type { FormSchema } from '~/landing/app/(garance)/cagnup/schema';

import {
  Button,
  Card,
  CardContent,
  Icon,
  Typography,
} from '@polyseme/design-system';
import { formatCurrency } from '@polyseme/utils/number/number-utils';
import { useTranslations } from 'next-intl';
import { forwardRef } from 'react';
import { useFormContext } from 'react-hook-form';

import {
  calculateTotalBudgetForAllCategories,
  calculateTotalCashbackForAllCategories,
} from '~/landing/app/(garance)/cagnup/calculations';
import { CategoryCard } from '~/landing/app/(garance)/cagnup/components';
import { categories } from '~/landing/app/(garance)/cagnup/constants';
import { Helper } from '~/landing/components';

type EstimatedSavingsProps = {
  scrollToCtaSection: () => void;
  scrollToFamilyForm: () => void;
};

export const EstimatedSavings = forwardRef<
  HTMLDivElement,
  EstimatedSavingsProps
>(({ scrollToCtaSection, scrollToFamilyForm }, ref) => {
  const form = useFormContext<FormSchema>();
  const t = useTranslations('landing.cagnup');

  const family = form.watch('family');

  const totalEstimatedBudget = calculateTotalBudgetForAllCategories(family);

  const totalEstimatedCashback = calculateTotalCashbackForAllCategories(family);

  const emptyFamily = Object.values(family).every((count) => count === 0);

  return (
    <section
      ref={ref}
      className="mx-auto w-full max-w-screen-2xl space-y-8 px-4 py-16 md:px-10"
    >
      <Typography variant="title-2">{t('estimatedSavings.title')}</Typography>

      <Card>
        <CardContent className="space-y-6 p-6">
          <div className="space-y-1">
            <Typography as="h3" variant="title-4">
              {t('estimatedSavings.card.title')}
            </Typography>
            <Typography className="text-muted-foreground" variant="helper">
              {t('estimatedSavings.card.subtitle')}
            </Typography>
          </div>

          <Card>
            <CardContent className="p-6 text-center">
              <Typography variant="title-2">
                {formatCurrency(totalEstimatedBudget)}
              </Typography>
              <Typography variant="body">
                {t('estimatedSavings.card.budget')}
              </Typography>
            </CardContent>
          </Card>

          {emptyFamily ? (
            <Card>
              <CardContent className="flex flex-col items-center justify-center bg-[#F9F9FA] p-6">
                <Icon className="size-10" name="shopping-cart" />
                <Typography
                  className="mb-6 mt-4 whitespace-pre-wrap"
                  variant="body"
                >
                  {t('estimatedSavings.card.emptyFamily.label')}
                </Typography>
                <Button onClick={scrollToFamilyForm}>
                  {t('estimatedSavings.card.emptyFamily.cta')}
                </Button>
              </CardContent>
            </Card>
          ) : (
            <>
              <ol className="grid gap-6 md:grid-cols-2 xl:grid-cols-3">
                {categories.map((category) => (
                  <CategoryCard key={category.id} category={category} />
                ))}
                <Card className="border-2 border-[#89C1C7] bg-[#E9F5F7] xl:col-span-3">
                  <CardContent className="flex h-full flex-col items-center p-6 text-center md:justify-center xl:justify-start">
                    <Typography className="mb-2" variant="title-4">
                      {t('estimatedSavings.footer.title')}
                    </Typography>
                    <Typography className="mb-6" variant="title-1">
                      {formatCurrency(totalEstimatedCashback)}
                    </Typography>
                    <Typography className="mb-10 text-muted-foreground">
                      {t('estimatedSavings.footer.helper')}
                    </Typography>
                    <Button onClick={scrollToCtaSection}>
                      {t('estimatedSavings.footer.cta')}{' '}
                      <Icon name="arrow-down" />
                    </Button>
                  </CardContent>
                </Card>
              </ol>
              <Helper>
                {t('estimatedSavings.information', {
                  date: new Date('08/01/2025').toLocaleDateString('fr-FR'),
                })}
              </Helper>
            </>
          )}
        </CardContent>
      </Card>
    </section>
  );
});

EstimatedSavings.displayName = 'EstimatedSavings';
